import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy } from "lucide-react";
import { api } from "@/lib/api";

const deviceId = () => {
  try {
    let d = localStorage.getItem("at_device");
    if (!d) { d = (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random()}`; localStorage.setItem("at_device", d); }
    return d;
  } catch { return ""; }
};

export const useTool = (endpoint) => {
  const started = useRef(Date.now());
  const trap = useRef(null);
  const [state, setState] = useState({ busy: false, data: null, error: null, limit: false });
  const run = async (body) => {
    setState((s) => ({ ...s, busy: true, error: null, limit: false }));
    try {
      const r = await api.post(endpoint, { ...body, device_id: deviceId(), website: trap.current?.value || "", elapsed_ms: Date.now() - started.current });
      setState({ busy: false, data: r.data, error: null, limit: false });
    } catch (e) {
      const msg = e?.response?.data?.detail;
      const text = typeof msg === "string" ? msg : "Something went wrong. Try again.";
      setState((s) => ({ ...s, busy: false, error: text, limit: e?.response?.status === 429 && /free generations/.test(text) }));
    }
  };
  return [state, run, trap];
};

export const Honeypot = ({ trap }) => (
  <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
    <label>Website<input ref={trap} name="website" tabIndex={-1} autoComplete="off" defaultValue="" /></label>
  </div>
);

export const Field = ({ label, id, textarea, hint, ...p }) => {
  const C = textarea ? "textarea" : "input";
  return (
    <label htmlFor={id} className="block text-sm">
      <span className="font-medium text-foreground">{label}</span>{hint && <span className="ml-2 text-xs text-muted-foreground">{hint}</span>}
      <C id={id} data-testid={id} {...p}
        className={`mt-2 w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-[15px] text-foreground placeholder:text-muted-foreground/60 transition-[border-color,box-shadow] focus:border-[#E9C98B]/50 focus:outline-none focus:ring-2 focus:ring-[#E9C98B]/20 ${textarea ? "min-h-[110px]" : "min-h-[48px]"}`} />
    </label>
  );
};

export const SubmitButton = ({ busy, label, testid }) => (
  <button type="submit" disabled={busy} data-testid={testid}
    className="btn-aurora inline-flex min-h-[48px] items-center rounded-full px-7 text-sm font-medium disabled:opacity-60">
    {busy ? "Working…" : label}
  </button>
);

export const CopyButton = ({ text, testid, label = "Copy" }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); } catch { /* clipboard blocked: user can still select the text */ }
    setDone(true); setTimeout(() => setDone(false), 1400);
  };
  return (
    <button type="button" onClick={copy} data-testid={testid} aria-label={`${label}: ${text}`}
      className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full border border-white/10 px-3 text-xs text-muted-foreground transition-colors hover:border-[#5EE6D0]/40 hover:text-foreground">
      {done ? <Check className="h-3.5 w-3.5 text-[#5EE6D0]" /> : <Copy className="h-3.5 w-3.5" />}{done ? "Copied" : label}
    </button>
  );
};

export const AiLabel = () => (
  <p data-testid="tool-ai-label" className="text-xs uppercase tracking-[0.16em] text-[#F3DDAE]/80">AI suggestions — validate with your own research</p>
);

export const ToolError = ({ state }) => state.error && (
  <div data-testid={state.limit ? "tool-limit-notice" : "tool-error"} role="alert"
    className="mt-6 rounded-xl border border-[#E9C98B]/30 bg-[#E9C98B]/[0.06] px-4 py-3 text-sm text-foreground">
    {state.error}
    {state.limit && <> <Link to="/#waitlist" data-testid="tool-limit-waitlist-link" className="text-[#5EE6D0] underline underline-offset-4">Join the founding beta</Link></>}
  </div>
);

export const SoftCta = () => (
  <aside data-testid="tool-soft-cta" className="mt-8 flex flex-col gap-3 rounded-2xl border border-[#E9C98B]/25 bg-gradient-to-br from-[#E9C98B]/[0.06] to-transparent p-5 sm:flex-row sm:items-center sm:justify-between">
    <p className="text-sm leading-relaxed text-foreground/90">Want this for every listing in your shop, with approval and undo?</p>
    <Link to="/#waitlist" data-testid="tool-soft-cta-link" className="inline-flex min-h-[44px] shrink-0 items-center rounded-full border border-[#E9C98B]/40 px-5 text-sm text-[#F3DDAE] transition-colors hover:bg-[#E9C98B]/10">Join the founding beta</Link>
  </aside>
);

export const Remaining = ({ data }) => data?.remaining !== undefined && (
  <p data-testid="tool-remaining" className="mt-3 text-xs text-muted-foreground">{data.remaining} of 5 free generations left today.</p>
);
